import { Toaster as Sonner } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;

// Roava-themed toasts. Tokens (--roava-green/red/orange/card/border/text) are
// declared in src/styles.css and follow the app's light/dark theme.
//
// Stack: capped at 3 visible; newest pushes the oldest out. Durations are
// tuned per type so quick acks feel snappy but actionable errors stay long
// enough to tap.
const baseToast =
  "group toast rounded-2xl border shadow-lg backdrop-blur-sm " +
  "group-[.toaster]:bg-[var(--roava-card)] group-[.toaster]:text-[var(--roava-text)] " +
  "group-[.toaster]:border-[var(--roava-border)]";

const Toaster = ({ ...props }: ToasterProps) => {
  return (
    <Sonner
      className="toaster group"
      position="bottom-center"
      offset="calc(env(safe-area-inset-bottom) + 96px)"
      mobileOffset={{ bottom: "calc(env(safe-area-inset-bottom) + 96px)" }}
      style={{ zIndex: 100000 } as React.CSSProperties}
      visibleToasts={3}
      gap={8}
      duration={2500}
      toastOptions={{
        classNames: {
          toast: baseToast,
          title: "font-medium",
          description: "group-[.toast]:text-[var(--roava-text-muted)]",
          actionButton:
            "group-[.toast]:bg-[var(--roava-blue)] group-[.toast]:text-[var(--roava-blue-foreground)] group-[.toast]:rounded-lg",
          cancelButton:
            "group-[.toast]:bg-[var(--roava-gray)] group-[.toast]:text-[var(--roava-gray-foreground)] group-[.toast]:rounded-lg",
          success:
            "group-[.toaster]:!bg-[color-mix(in_oklab,var(--roava-green)_12%,var(--roava-card))] " +
            "group-[.toaster]:!border-[color-mix(in_oklab,var(--roava-green)_35%,transparent)] " +
            "group-[.toaster]:!text-[var(--roava-text)] " +
            "[&_[data-icon]]:!text-[var(--roava-green)] [&_svg]:!text-[var(--roava-green)]",
          error:
            "group-[.toaster]:!bg-[color-mix(in_oklab,var(--roava-red)_12%,var(--roava-card))] " +
            "group-[.toaster]:!border-[color-mix(in_oklab,var(--roava-red)_35%,transparent)] " +
            "group-[.toaster]:!text-[var(--roava-text)] " +
            "[&_[data-icon]]:!text-[var(--roava-red)] [&_svg]:!text-[var(--roava-red)]",
          warning:
            "group-[.toaster]:!bg-[color-mix(in_oklab,var(--roava-orange)_14%,var(--roava-card))] " +
            "group-[.toaster]:!border-[color-mix(in_oklab,var(--roava-orange)_35%,transparent)] " +
            "group-[.toaster]:!text-[var(--roava-text)] " +
            "[&_[data-icon]]:!text-[var(--roava-orange)] [&_svg]:!text-[var(--roava-orange)]",
          info:
            "group-[.toaster]:!bg-[color-mix(in_oklab,var(--roava-blue)_12%,var(--roava-card))] " +
            "group-[.toaster]:!border-[color-mix(in_oklab,var(--roava-blue)_35%,transparent)] " +
            "group-[.toaster]:!text-[var(--roava-text)] " +
            "[&_[data-icon]]:!text-[var(--roava-blue)] [&_svg]:!text-[var(--roava-blue)]",
        },
      }}
      {...props}
    />
  );
};


export { Toaster };
